import type { ComponentPropsWithoutRef, ReactElement, ReactNode } from 'react';
import {
  CalendarDays,
  Check,
  CheckCircle2,
  CheckSquare,
  Eye,
  MessageSquare,
  Paperclip,
  Text,
  ThumbsUp,
  X,
} from 'lucide-react';
import type { BoardFaceList, CardCustomFieldBadge } from '@molt/shared';
import { dueStatusLabel, type DueStatus } from '@/lib/card-dates';
import { cn } from '@/lib/cn';
import { useInterfaceLanguage } from '@/lib/interface-language';
import { formatLocaleCardDate, formatLocaleCardDueDate } from '@/lib/locale-formatting';
import { useLocalePreferencesValue } from '@/lib/locale-preferences';
import type { PowerUpSurfaceSummary } from '@/lib/power-ups';
import { AvatarStack } from '../Avatar';

export interface CardBadgesProps {
  card: BoardFaceList['cards'][number];
  dueStatus: DueStatus;
  dueAt: string | null;
  /** BE-44.7 (FR-EXP-029): date-only dues render as the same calendar date for everyone. */
  dueAllDay: boolean;
  powerUpSurfaces?: PowerUpSurfaceSummary | undefined;
  /**
   * `FR-CARD-005`'s card reference (`MG-0001`), drawn at the right end of the
   * row — `null` when the board hides references or the viewer has none
   * (docs/notes/card-keys.md, "Web").
   */
  cardRef?: string | null | undefined;
  /**
   * Member initials (`docs/notes/member-initials.md`) — props for the member
   * avatar row's root, from `CardTile`: the lift above the tile's stretched
   * link (`relative z-[3]`) and the handlers that keep a click, a right-click
   * and a drag starting on an avatar doing what they do on the rest of the
   * card. Undefined where nothing covers the row (an optimistic card, a bare
   * render), and then the row is exactly what it was.
   */
  memberRowProps?: ComponentPropsWithoutRef<'span'> | undefined;
  /** The board's dangerous-comments threshold is reached: the comment badge turns red and says so. */
  dangerous?: boolean | undefined;
}

export function CardBadges(_props: CardBadgesProps): ReactElement | null {
  const {
    card,
    dueStatus,
    dueAt,
    dueAllDay,
    powerUpSurfaces,
    cardRef = null,
    memberRowProps,
    dangerous = false,
  } = _props;
  const localePreferences = useLocalePreferencesValue();
  const { t } = useInterfaceLanguage();
  const dueLabel =
    dueAt === null
      ? null
      : formatLocaleCardDueDate(t, dueAt, localePreferences, dueAllDay);
  const badges: ReactElement[] = [];

  if (card.hasDescription) {
    badges.push(
      <BadgeItem key="description" label={t('board.card.badge.description')}>
        <Text aria-hidden="true" className="size-3.5" />
        <span className="sr-only">{t('board.card.badge.descriptionText')}</span>
      </BadgeItem>,
    );
  }

  if (card.commentCount > 0) {
    badges.push(
      <BadgeItem
        key="comments"
        label={
          t(
            'board.card.badge.comments',
            { count: card.commentCount },
            card.commentCount,
          ) + (dangerous ? `. ${t('board.card.badge.dangerousComments')}` : '')
        }
      >
        <MessageSquare aria-hidden="true" className="size-3.5" />
        <span className={dangerous ? 'font-semibold text-bad' : undefined}>
          {card.commentCount}
        </span>
      </BadgeItem>,
    );
  }

  if (card.attachmentCount > 0) {
    badges.push(
      <BadgeItem
        key="attachments"
        label={t(
          'board.card.badge.attachments',
          { count: card.attachmentCount },
          card.attachmentCount,
        )}
      >
        <Paperclip aria-hidden="true" className="size-3.5" />
        <span>{card.attachmentCount}</span>
      </BadgeItem>,
    );
  }

  if (card.voteCount !== null && card.voteCount > 0) {
    badges.push(
      <BadgeItem
        key="votes"
        label={t('board.card.badge.votes', { count: card.voteCount }, card.voteCount)}
      >
        <ThumbsUp aria-hidden="true" className="size-3.5" />
        <span>{card.voteCount}</span>
      </BadgeItem>,
    );
  }

  if (card.approvals !== null) {
    const { approved, rejected, required, met } = card.approvals;
    badges.push(
      <BadgeItem
        key="approvals"
        label={t(met ? 'board.card.badge.approvalsMet' : 'board.card.badge.approvals', {
          approved,
          required,
        })}
        className={met ? 'text-ok' : undefined}
      >
        <Check aria-hidden="true" className="size-3.5" />
        <span>
          {approved}/{required}
        </span>
      </BadgeItem>,
    );
    if (rejected > 0) {
      badges.push(
        <BadgeItem
          key="rejections"
          label={t('board.card.badge.rejections', { count: rejected }, rejected)}
        >
          <X aria-hidden="true" className="size-3.5" />
          <span>{rejected}</span>
        </BadgeItem>,
      );
    }
  }

  if (card.checklistItemCount > 0) {
    const label = t('board.card.badge.checklist', {
      checked: card.checklistCheckedItemCount,
      total: card.checklistItemCount,
    });
    badges.push(
      <BadgeItem key="checklists" label={label}>
        <CheckSquare aria-hidden="true" className="size-3.5" />
        <span>
          {card.checklistCheckedItemCount}/{card.checklistItemCount}
        </span>
      </BadgeItem>,
    );
  }

  if (dueStatus !== 'none') {
    badges.push(
      <BadgeItem
        key="due"
        /* `dueLabel === null` is the same test as `dueAt === null` one line
           up — `dueLabel` is derived from `dueAt` in this render — but it is
           the one TypeScript narrows, so `{date}` gets a `string` rather than
           a `?? ''` that would render an empty slot if the two ever diverged. */
        label={
          dueLabel === null
            ? dueStatusLabel(t, dueStatus)
            : t('board.card.badge.due', {
                date: dueLabel,
                status: dueStatusLabel(t, dueStatus),
              })
        }
        className={dueStateClasses(dueStatus)}
      >
        {dueStatus === 'complete' ? (
          <CheckCircle2 aria-hidden="true" className="size-3.5" />
        ) : (
          <CalendarDays aria-hidden="true" className="size-3.5" />
        )}
        {dueLabel === null ? null : <span>{dueLabel}</span>}
      </BadgeItem>,
    );
  }

  for (const customField of card.customFieldBadges) {
    const value = customFieldBadgeValue(t, customField, (value) =>
      formatLocaleCardDate(t, value, localePreferences),
    );
    badges.push(
      <BadgeItem
        key={`custom-field-${customField.definitionId}`}
        label={t('board.card.badge.customField', { name: customField.name, value })}
        className="max-w-full rounded-[6px] border border-border bg-surface-raised px-1.5 py-0.5 text-ink-muted"
      >
        <span className="min-w-0 truncate">
          <span className="sr-only">
            {t('board.card.badge.customFieldPrefix', { name: customField.name })}
          </span>
          {customField.value.type === 'checkbox' ? customField.name : value}
        </span>
      </BadgeItem>,
    );
  }

  for (const entry of powerUpSurfaces?.cardFrontBadges ?? []) {
    badges.push(
      <BadgeItem
        key={`power-up-${entry.id}`}
        label={t('board.card.badge.powerUp', {
          powerUpName: entry.powerUpName,
          label: entry.label,
        })}
        className="max-w-full rounded-[6px] border border-border bg-surface-raised px-1.5 py-0.5 text-ink-muted"
      >
        <span className="min-w-0 truncate">{entry.label}</span>
      </BadgeItem>,
    );
  }

  /**
   * The watch eye — `FR-BOARD-030` AC4 and `FR-COLLAB-022`, and the consumer
   * of `boardFaceCardSchema.watching`, the field this contract chunk added.
   *
   * ⚠ **LAST IN THE BADGE ARRAY AND THEREFORE IMMEDIATELY BEFORE THE
   * `AvatarStack`, WHICH IS THE ORDER `FR-BOARD-030` ENUMERATES BY NAME**:
   * "a description indicator ..., a comments count, an attachments count,
   * checklist progress ..., a due-date badge ..., a watch (eye) indicator
   * when the viewer is watching the card, and member avatars aligned to the
   * right." It is pushed after the custom-field and Power-Up loops for that
   * reason — those two are extensions the requirement's list predates, and
   * putting the eye before them would move it out of the named order.
   *
   * ⚠ **`=== true`, NOT TRUTHINESS, AND THE THIRD STATE IS THE POINT.**
   * `watching` is `boolean | null`: `null` means the server declined to
   * answer for this caller (no session — `GET /boards/:id/contents` is
   * anonymous-capable and must stay so), and `false` means this viewer is
   * not watching. Both render nothing, on `voteCount`'s `!== null &&` footing
   * four branches up and on `FR-BOARD-030`'s "badges appear only when the
   * underlying content exists".
   *
   * ⚠ **`aria-label` ALONE — no `title`, no `sr-only` twin.** The two-key
   * pattern six branches up (`description` + `descriptionText`) exists
   * because that badge renders a visible text node; this one renders an
   * `aria-hidden` icon and nothing else, so an `sr-only` copy would announce
   * the same string twice. `ListHeader`'s list eye and `BoardFace`'s board
   * eye are the house pattern and both do exactly this.
   *
   * ⚠ **IT JOINS THE EXISTING `flex-wrap` ROW AND ADDS NO CHROME.** No gap,
   * no padding, no new row: `docs/notes/density-budget.md` measured 2.4px of
   * headroom under 24 cards at 1440x900, and "three more pixels of chrome
   * anywhere in the column drops the board from 24 to 20". A `size-3.5` icon
   * inside the row that is already there costs a watched card nothing, and
   * costs an unwatched card nothing at all because it does not render.
   */
  if (card.watching === true) {
    badges.push(
      <BadgeItem key="watching" label={t('board.card.badge.watching')}>
        <Eye aria-hidden="true" className="size-3.5" />
      </BadgeItem>,
    );
  }

  if (badges.length === 0 && card.members.length === 0 && cardRef === null) return null;

  return (
    <div className="flex flex-wrap items-center gap-1.5 text-xs text-ink-muted">
      {badges.length > 0 ? (
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">{badges}</div>
      ) : (
        <div className="min-w-0 flex-1" />
      )}
      {/*
        Member initials — hovering a circle names the member
        (`primitives.avatar.tooltip`: "Ben Edwards · @bedwards", or the
        `profileName` chain when either half is empty). The circle draws the
        server's `initials`, which is the superadmin-set override when there is
        one. `names="tooltip"` only reaches a pointer because `CardTile` lifts
        this row above its stretched link through `memberRowProps`; the board
        face's one `TooltipProvider` serves every tooltip here.
      */}
      {card.members.length > 0 ? (
        <AvatarStack
          {...memberRowProps}
          members={card.members}
          overflowAction="none"
          size="xs"
          names="tooltip"
        />
      ) : null}
      {cardRef !== null ? (
        <span data-card-ref className="shrink-0 tabular-nums">
          {cardRef}
        </span>
      ) : null}
    </div>
  );
}

function BadgeItem({
  label,
  className,
  children,
}: {
  label: string;
  className?: string | undefined;
  children: ReactNode;
}) {
  return (
    <span
      aria-label={label}
      className={cn('inline-flex items-center gap-1 font-medium', className)}
    >
      {children}
    </span>
  );
}

function dueStateClasses(state: DueStatus): string {
  switch (state) {
    case 'complete':
      return 'text-ok';
    case 'due-soon':
      return 'text-warn';
    case 'overdue':
    case 'long-overdue':
      return 'text-bad';
    case 'neutral':
    case 'none':
      return 'text-ink-muted';
  }
}

/**
 * FE-80.1 — `t` is positional and first, and it is NOT optional. The previous
 * signature took `formatDate` with a default, which is why the second caller
 * (`cardAccessibleDescription`, `CardTile.tsx`) was quietly rendering a raw
 * Postgres timestamp for a `date` badge; a required translator makes both
 * callers pass what they have rather than fall through to a default that
 * looked correct.
 *
 * ⚠ `checkedValue` is byte-identical to nothing: `cardBack.customFields.
 * checkedValue` is `Checked` (capital C, a standalone value in a field row)
 * and this one is `checked` (lower case, always the tail of a
 * `{name}: {value}` badge). Two keys, two values, measured against the
 * catalogue rather than assumed from the shared word.
 */
export function customFieldBadgeValue(
  t: (key: string, values?: Record<string, string | number>, count?: number) => string,
  badge: CardCustomFieldBadge,
  formatDate: (value: string) => string = (value) => value,
): string {
  switch (badge.value.type) {
    case 'text':
      return badge.value.value;
    case 'number':
      return String(badge.value.value);
    case 'checkbox':
      return t('board.card.badge.checkedValue');
    case 'date':
      return formatDate(badge.value.value);
    case 'dropdown':
      return badge.value.label;
  }
}
